import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Cloudflare Turnstile widget.
//
// Supabase Auth forms pass the token through `options.captchaToken`. App-owned
// API forms pass it in their request body and must validate it server-side
// against Cloudflare's Siteverify endpoint.
//
// Tokens are SINGLE USE and expire after 300s: call `reset()` after each
// submit attempt (success or failure) or the next attempt fails with
// "captcha protection: request disallowed".
//
// When NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset the component renders nothing
// and `getToken()` resolves `undefined`, allowing Turnstile to be disabled in
// local environments and rolled out by configuration.

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// Cloudflare hands the token to the callback; a slow network or a challenge
// the user ignores must not hang the form forever.
const TOKEN_TIMEOUT_MS = 30000;

type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  appearance?: TurnstileAppearance;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: TurnstileRenderOptions,
      ) => string | undefined;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export const TURNSTILE_SITE_KEY =
  process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

export const isTurnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

// Shown when the widget never produced a token. In practice this is almost
// never a real bot — it is an ad blocker, a privacy extension or a corporate
// network blocking challenges.cloudflare.com, so the copy names the actual
// fixes instead of just saying "try again" and leaving the user stuck.
export const TURNSTILE_ERROR_MESSAGE =
  "We couldn't complete the security check. This is usually caused by an ad " +
  'blocker, a privacy extension, or a work network blocking Cloudflare. Try ' +
  'disabling those for this page, or use a different browser or a private ' +
  'window. If it still fails, email hey@easy-peasy.ai and we will sign you in.';

// Same guidance, shortened for the inline hint under a widget that failed to
// load — at that point the user has not submitted anything yet.
const TURNSTILE_INLINE_HINT =
  "The security check didn't load. It's usually an ad blocker, a privacy " +
  'extension, or a work network blocking Cloudflare.';

let scriptPromise: Promise<void> | null = null;

const loadTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const onError = () => {
      // Allow a later attempt to retry the load.
      scriptPromise = null;
      reject(new Error('Failed to load Turnstile'));
    };

    const existing = document.getElementById(
      SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export type TurnstileHandle = {
  /**
   * Resolves with a fresh token, waiting for the challenge to finish if it is
   * still running. Resolves `undefined` when Turnstile is not configured, or
   * when it failed / timed out — callers should treat that as a hard failure
   * whenever `isTurnstileEnabled` is true.
   */
  getToken: () => Promise<string | undefined>;
  /** Discards the current token and re-runs the widget. */
  reset: () => void;
};

type TurnstileProps = {
  /** Labels the request in the Cloudflare analytics, e.g. 'login'. */
  action?: string;
  className?: string;
  /** Keeps inline recovery guidance relevant outside authentication forms. */
  context?: 'auth' | 'report' | 'contact';
  theme?: 'auto' | 'light' | 'dark';
  /**
   * Cloudflare's `appearance`. `interaction-only` keeps the widget invisible
   * unless the visitor has to click it; the challenge, the token and every
   * check are the same. The auth forms use it, other forms keep `always`.
   */
  appearance?: TurnstileAppearance;
  /** Replaces the built-in English hint shown when the widget fails. */
  failureHint?: ReactNode;
  /**
   * Fires whenever the widget produces a token (or `undefined` on failure).
   * Only needed for push-style consumers such as the /turnstile page the
   * mobile app loads in a WebView; forms use the imperative `getToken()`.
   */
  onToken?: (token: string | undefined) => void;
};

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  (
    {
      action,
      className,
      context = 'auth',
      theme = 'auto',
      appearance = 'always',
      failureHint,
      onToken,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | undefined>(undefined);
    // Surfaced inline so a user whose widget never renders is told why,
    // rather than only finding out when the sign-in button rejects them.
    const [failed, setFailed] = useState(false);
    // Mirror of `failed` for getToken(), which runs from an imperative handle
    // with no access to current state.
    const failedRef = useRef(false);
    const tokenRef = useRef<string | null>(null);
    const waitersRef = useRef<Array<(token: string | undefined) => void>>([]);

    // Kept in a ref so a caller passing an inline arrow does not tear down and
    // re-render the widget on every parent render.
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    const settleWaiters = useCallback(
      (token: string | undefined, notify = true) => {
        const waiters = waitersRef.current;
        waitersRef.current = [];
        waiters.forEach((resolve) => resolve(token));
        // Unmount flushes pending waiters but must not report a failure.
        if (notify) onTokenRef.current?.(token);
      },
      [],
    );

    useEffect(() => {
      if (!isTurnstileEnabled) return;

      let cancelled = false;

      loadTurnstileScript()
        .then(() => {
          if (cancelled || !containerRef.current || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            action,
            theme,
            appearance,
            callback: (token) => {
              tokenRef.current = token;
              failedRef.current = false;
              setFailed(false);
              settleWaiters(token);
            },
            'error-callback': () => {
              tokenRef.current = null;
              failedRef.current = true;
              setFailed(true);
              settleWaiters(undefined);
            },
            'timeout-callback': () => {
              tokenRef.current = null;
              failedRef.current = true;
              setFailed(true);
              settleWaiters(undefined);
            },
            'expired-callback': () => {
              // Tokens live 300s; get a new one so a form left open still works.
              tokenRef.current = null;
              if (widgetIdRef.current) {
                window.turnstile?.reset(widgetIdRef.current);
              }
            },
          });
        })
        .catch(() => {
          // The script is blocked outright — error-callback never fires, so
          // this is the only place that failure becomes visible.
          if (!cancelled) {
            failedRef.current = true;
            setFailed(true);
            settleWaiters(undefined);
          }
        });

      return () => {
        cancelled = true;
        settleWaiters(undefined, false);
        if (widgetIdRef.current) {
          window.turnstile?.remove(widgetIdRef.current);
          widgetIdRef.current = undefined;
        }
      };
    }, [action, theme, appearance, settleWaiters]);

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => {
          if (!isTurnstileEnabled) return Promise.resolve(undefined);
          if (tokenRef.current) return Promise.resolve(tokenRef.current);
          // Already known broken (blocked script, failed challenge, wrong
          // hostname) — fail now instead of parking the form for 30s.
          if (failedRef.current) return Promise.resolve(undefined);

          return new Promise<string | undefined>((resolve) => {
            let settled = false;
            const waiter = (token: string | undefined) => {
              if (settled) return;
              settled = true;
              clearTimeout(timer);
              resolve(token);
            };
            const timer = setTimeout(() => {
              waitersRef.current = waitersRef.current.filter(
                (entry) => entry !== waiter,
              );
              waiter(undefined);
            }, TOKEN_TIMEOUT_MS);
            waitersRef.current.push(waiter);
          });
        },
        reset: () => {
          tokenRef.current = null;
          failedRef.current = false;
          setFailed(false);
          if (widgetIdRef.current) {
            window.turnstile?.reset(widgetIdRef.current);
          }
        },
      }),
      [],
    );

    if (!isTurnstileEnabled) return null;

    return (
      <div className={className}>
        {/* Own column so the failure hint stacks under the widget even when
            the caller passes a row layout such as `flex justify-center`. */}
        <div className='flex flex-col items-center'>
          <div ref={containerRef} />
          {failed && failureHint}
          {failed && !failureHint && (
            <p className='mt-2 max-w-[300px] text-center text-xs leading-relaxed text-amber-700 dark:text-amber-400'>
              {TURNSTILE_INLINE_HINT}{' '}
              <a
                href={
                  context === 'report'
                    ? 'mailto:support@easy-peasy.ai?subject=Cannot%20submit%20image%20report%20-%20security%20check'
                    : context === 'contact'
                      ? 'mailto:hey@easy-peasy.ai?subject=Cannot%20submit%20form%20-%20security%20check'
                      : 'mailto:hey@easy-peasy.ai?subject=Cannot%20sign%20in%20-%20security%20check'
                }
                className='font-medium underline'
              >
                {context === 'contact' ? 'Email us directly' : 'Contact us'}
              </a>{' '}
              {context === 'report'
                ? 'to submit the report manually.'
                : context === 'contact'
                  ? 'instead.'
                  : "and we'll sign you in."}
            </p>
          )}
        </div>
      </div>
    );
  },
);

Turnstile.displayName = 'Turnstile';

export default Turnstile;
